import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { P, ghost } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Ico, L } from '@/design-system/icons';

const CLIPBOARD = [
  'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
  'M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z',
];

interface TabDef {
  path: string;
  iconPaths: string[];
  labelKey: string;
}

const TABS: TabDef[] = [
  { path: '/dashboard', iconPaths: [...L.lines], labelKey: 'nav.dashboard' },
  { path: '/budget', iconPaths: CLIPBOARD, labelKey: 'nav.budget' },
  { path: '/patrimoine', iconPaths: [...L.shield], labelKey: 'nav.patrimoine' },
  { path: '/objectifs', iconPaths: [...L.target], labelKey: 'nav.objectifs' },
];

function getContentMaxWidth(pathname: string): string | undefined {
  if (pathname.startsWith('/dashboard')) return undefined;
  if (pathname.startsWith('/parametres')) return '700px';
  return '900px';
}

export function DesktopLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const maxWidth = getContentMaxWidth(pathname);

  return (
    <div style={{ minHeight: '100dvh', background: P.s0 }}>
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 30,
        width: '100%',
        height: 48,
        background: P.s0,
        borderBottom: `1px solid ${P.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        fontFamily: sans,
        boxSizing: 'border-box',
      }}>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {TABS.map((tab) => {
            const active = pathname.startsWith(tab.path);
            const color = active ? P.accent : P.t2;
            return (
              <button
                key={tab.path}
                onClick={() => navigate(tab.path)}
                style={{
                  background: active ? ghost.accent.bg : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 6,
                  color,
                  fontFamily: sans,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <Ico paths={tab.iconPaths} size={16} color={color} />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </nav>
        <button
          onClick={() => navigate('/parametres')}
          aria-label={t('nav.parametres')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Ico paths={[...L.settingsArr]} size={20} color={P.t2} />
        </button>
      </header>

      <main style={{
        paddingTop: 48,
        maxWidth,
        marginLeft: maxWidth ? 'auto' : undefined,
        marginRight: maxWidth ? 'auto' : undefined,
      }}>
        <Outlet />
      </main>
    </div>
  );
}
